import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { SectionBase } from './SectionBase';
import styled from 'styled-components';
import { Program, Report } from './SectionPrograms';
import Selector, {
  MAX_WIDTH_FOR_SELECTOR,
} from 'shared/design-system/components/Selector';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import Select from '@mui/material/Select';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { GuidanceTextResponse } from './utils';

interface ProgramOption extends Program {
  yearsToRender: number[];
}

interface Value {
  id: string; // program id
  value: number | number[];
}

interface SelectedOptions {
  [x: string]: Value;
}

interface Q5Value {
  id: string;
  value: 'Yes' | 'No';
}

interface SectionProgramYearProps {
  title: string;
  subTitle: string;
  helpText: string;
  questionId: number;
  guidanceText: GuidanceTextResponse;
  onChange: (questionId: number, value: Value[]) => unknown;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: any;
  options: Program[];
  value: Value[] | undefined;
  allValues: {
    [x: string]: unknown;
    [20005]: Q5Value[];
  };
  accountName: string; // donor name
}

const getRenderOptions = (
  back: number,
  programs: Program[],
  allValues: Q5Value[],
): ProgramOption[] => {
  const year = new Date().getFullYear() + Math.ceil(back / 2);
  const years = Array.from({ length: back }, (v, i) => year - back + i + 1);

  const result: ProgramOption[] = [];
  programs.forEach(p => {
    if (allValues.find(v => v.id === p.id.toString())?.value === 'Yes') {
      return;
    }
    const includedYears: number[] = [];
    years.forEach(y => {
      if (p.reports?.findIndex(r => r.year === y) === -1) {
        includedYears.push(y);
      }
    });
    result.push({ ...p, yearsToRender: includedYears });
  });
  return result;
};

const parseValue = (value: Value[] | undefined): SelectedOptions => {
  const result: SelectedOptions = {};
  value?.forEach(v => {
    result[v.id] = v;
  });
  return result;
};

const isReportSelected = (
  value: Value | undefined,
  reportId: number,
): boolean => {
  if (!value || typeof value.value === 'number') return false;
  return value.value.includes(reportId);
};

const disabledText =
  'This report is fully allocated and cannot accept more donations than it\'s budget';

export const SectionProgramYear: React.FC<SectionProgramYearProps> = ({
  title,
  subTitle,
  helpText,
  error,
  options,
  guidanceText,
  questionId,
  value,
  onChange,
  allValues,
}) => {
  const renderOptions = useMemo(
    () => getRenderOptions(10, options, allValues[20005]),
    [options, allValues],
  );
  const [selected, setSelected] = useState<SelectedOptions>(parseValue(value));

  useEffect(() => {
    const values = Object.keys(selected).reduce((res: Value[], k) => {
      const val = selected[k].value;
      if (typeof val === 'number' || val.length !== 0) {
        res.push(selected[k]);
      }
      return res;
    }, []);
    if (
      values.length < allValues[20005]?.filter(x => x.value === 'No')?.length
    ) {
      onChange(questionId, []);
    } else {
      onChange(questionId, values);
    }
  }, [selected]);

  const handleReportClick = useCallback(
    (program: ProgramOption, report: Report) => {
      setSelected(s => {
        const newState = { ...s };
        const value = newState[program.id];
        if (!value || typeof value.value === 'number') {
          newState[program.id] = {
            id: program.id.toString(),
            value: [report.id],
          };
          return newState;
        }
        const index = value.value.findIndex(x => x === report.id);
        if (index === -1) {
          value.value.push(report.id);
        } else {
          value.value.splice(index, 1);
        }
        return newState;
      });
    },
    [setSelected],
  );

  const handleDropdown = useCallback(
    (program: ProgramOption, year?: number) => {
      const value: number = year || program.yearsToRender[0];
      if (!value) return;
      setSelected(s => {
        const newState = { ...s };
        newState[program.id] = {
          id: program.id.toString(),
          value,
        };
        return newState;
      });
    },
    [setSelected],
  );

  return (
    <SectionBase
      title={title}
      subTitle={subTitle}
      helpText={helpText}
      error={error}
      questionId={questionId}
      guidanceText={guidanceText}
    >
      <StyledDiv>
        <div className="header-text">
          <label className="regular-3">Select all that apply.</label>
        </div>
        {renderOptions.map((renderOption: ProgramOption) => {
          const isNotListedSelected =
            typeof selected[renderOption.id]?.value === 'number';
          return (
            <>
              <TrueImpactText variant="body-bold" className="program-container">
                {renderOption.name}
              </TrueImpactText>
              {renderOption.reports?.map(o => {
                return (
                  <Selector
                    variant="checkbox"
                    label={o.year}
                    selected={isReportSelected(selected[renderOption.id], o.id)}
                    disabled={o.fully_allocated}
                    titleText={o.fully_allocated ? disabledText : undefined}
                    maxWidth={MAX_WIDTH_FOR_SELECTOR}
                    padding={SPACERS.tiny}
                    margin={`${SPACERS.tiny} auto`}
                    onClick={() => handleReportClick(renderOption, o)}
                    className="select-option"
                  />
                );
              })}
              {renderOption.yearsToRender.length !== 0 && (
                <>
                  <Selector
                    variant="checkbox"
                    label="Not listed"
                    selected={isNotListedSelected}
                    maxWidth={MAX_WIDTH_FOR_SELECTOR}
                    padding={SPACERS.tiny}
                    margin={`${SPACERS.tiny} auto`}
                    onClick={() => handleDropdown(renderOption)}
                    className="select-option"
                  />
                  {isNotListedSelected && (
                    <Select
                      native
                      value={selected[renderOption.id]?.value}
                      onChange={e => {
                        const year = parseInt(e.target.value as string);
                        year && handleDropdown(renderOption, year);
                      }}
                      className="mui-select"
                    >
                      {renderOption.yearsToRender.map(y => (
                        <option value={y}>{y}</option>
                      ))}
                    </Select>
                  )}
                </>
              )}
            </>
          );
        })}
      </StyledDiv>
    </SectionBase>
  );
};

const StyledDiv = styled.div`
  width: 100%;
  .program-container {
    margin-top: ${SPACERS.large};
    margin-bottom: ${SPACERS.small};
    padding-top: ${SPACERS.small};
    padding-bottom: ${SPACERS.small};
    padding-left: 15px;
    padding-right: 15px;
    border: solid 1px #cbd6e2;
    border-radius: 4px;
  }
  .header-text {
    text-align: center;
    margin-top: 20px;
    margin-bottom: 10px;
  }
  .select-option {
    height: unset;
  }
  .mui-select {
    width: 100%;
    svg.MuiSvgIcon-root.MuiSelect-icon {
      right: 10px !important;
    }
  }
`;
